import {
  type AnnulOperation,
  type BranchChangeset,
  type GlMapCreateOperation,
  type GlMapInOut,
  isGlMapCreateOperation,
  isGlMapHideOperation,
} from '@festina/configuration-general-types/configuration'
import type { Tree } from '@festina-life/api'
import { dateUtils, type Optional } from '@festina-life/core'

import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import { createOperationFindFn } from '../helpers'
import { constructIndexedOperations } from '../indexedOperations/helpers'
import type { WorkspaceOperationOf } from '../types'
import {
  type GlMapOperation,
  type IndexedGlMapOperations,
  type IndexedGlMapOperationWithMetaData,
  isGlMapOperation,
} from './types'

export const getGlMapOperationMappingId = (operation: GlMapOperation) =>
  isGlMapCreateOperation(operation)
    ? operation.glMap?.mappingId
    : operation.mappingId

export const getGlMapOperationFromDate = (operation: GlMapOperation) =>
  isGlMapCreateOperation(operation) ? operation.glMap?.from : operation.fromDate

export const isHideOnlyGlMap = (
  operations: IndexedGlMapOperations['operations'],
) =>
  !!operations.length &&
  operations.every(({ internalOperation }) =>
    isGlMapHideOperation(internalOperation),
  )

export const glMapCreateOperationFromInOut = (
  glMap: GlMapInOut,
): GlMapCreateOperation => ({
  _type: 'GlMapCreateOperation',
  glMap,
})

export const glMapOperationFindIndex = createOperationFindFn(
  (operation: IndexedGlMapOperationWithMetaData) => {
    const fromDate = getGlMapOperationFromDate(operation.internalOperation)
    return fromDate
      ? dateUtils.dateUtcAsLocal(fromDate).valueOf()
      : Number.NEGATIVE_INFINITY
  },
)

/**
 * A GL-map only applies at the given date once at least one of its versions has
 * come into effect. Versions without a `from` date are always in effect.
 */
export const isGlMapActiveAtDate =
  (viewAt: Date) => (value: Optional<IndexedGlMapOperations>) =>
    !!value?.operations.some((operation) => {
      const fromDate = getGlMapOperationFromDate(operation.internalOperation)

      return (
        !fromDate ||
        dateUtils.dateUtcAsLocal(fromDate).valueOf() <= viewAt.getTime()
      )
    })

/**
 * Resolves the GL-map version that is in effect at the given date, i.e. the last
 * operation whose `from` date is not in the future relative to `atDate`.
 */
export const getGlMapOperationAtDate = (
  indexedGlMapOperations: Optional<IndexedGlMapOperations>,
  atDate: Date,
) => {
  const comparableDate = atDate.getTime()

  return (indexedGlMapOperations?.operations ?? []).findLast((compareTo) => {
    const compareToFromDate = getGlMapOperationFromDate(
      compareTo.internalOperation,
    )
    return (
      !compareToFromDate ||
      dateUtils.dateUtcAsLocal(compareToFromDate).valueOf() <= comparableDate
    )
  })
}

export const getLastCreatedEntity = (
  operations: IndexedGlMapOperationWithMetaData[],
): Optional<GlMapInOut> =>
  operations
    .map(({ internalOperation }) => internalOperation)
    .findLast(isGlMapCreateOperation)?.glMap

export const constructIndexedGlMapOperations = ({
  configurationChangesets,
  draftGlMapOperations,
  draftAnnulOperations,
  blockTopology,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  draftGlMapOperations: Optional<WorkspaceOperationOf<GlMapOperation>[]>
  draftAnnulOperations?: WorkspaceOperationOf<AnnulOperation>[]
  blockTopology: Tree
}): ConfigurationItemsByBlock<IndexedGlMapOperations> =>
  constructIndexedOperations<GlMapOperation, IndexedGlMapOperations>({
    configurationChangesets,
    draftOperations: draftGlMapOperations,
    draftAnnulOperations,
    blockTopology,
    isIndexedOperation: isGlMapOperation,
    getEntityId: getGlMapOperationMappingId,
    createDefault: (mappingId) => ({
      mappingId,
      // These fields are updated once all operations have been added.
      lastEntity: undefined,
      isDraft: false,
      operations: [],
    }),
    findIndexFn: glMapOperationFindIndex,
    postProcessEntity: (indexedGlMap) => {
      indexedGlMap.lastEntity = getLastCreatedEntity(indexedGlMap.operations)
    },
  })
